import { useState, useRef, KeyboardEvent, MouseEvent } from 'react'
import InlineRenameInput from '@/components/InlineRenameInput'
import { Pencil } from 'lucide-react'
import clsx from 'clsx'
import { DEFAULT_MAX_STRING_LENGTH, trimAndClamp } from '@/utils/string'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface EditableTitleProps {
  title: string
  onSave: (newTitle: string) => void
  className?: string
  maxLength?: number
  isRTL?: boolean
  variant?: 'large' | 'small' // Controls styling - 'large' for page titles, 'small' for table cells
  stopPropagation?: boolean // Stop click event propagation (useful in tables)
}

const EditableTitle = ({
  title,
  onSave,
  className,
  maxLength = DEFAULT_MAX_STRING_LENGTH,
  isRTL = false,
  variant = 'large',
  stopPropagation = false,
}: EditableTitleProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedTitle, setEditedTitle] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const isActionInProgressRef = useRef(false) // Prevent blur race conditions
  // Use the full title for display — let CSS `truncate` handle visual overflow.
  // JS-level truncation was cutting names too short (50 chars default).
  const trimmed = title.trim()

  const startEditing = () => {
    setEditedTitle(title)
    setIsEditing(true)
    setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 0)
  }

  const handleSave = () => {
    if (isActionInProgressRef.current) return
    isActionInProgressRef.current = true
    
    const sanitizedTitle = trimAndClamp(editedTitle, maxLength)
    
    if (!sanitizedTitle) {
      // If empty, revert to original title
      setEditedTitle(title)
      setIsEditing(false)
      // Delay resetting the flag to prevent duplicate saves
      setTimeout(() => {
        isActionInProgressRef.current = false
      }, 0)
      return
    }
    
    if (sanitizedTitle !== title) {
      onSave(sanitizedTitle)
    }
    setIsEditing(false)
    // Delay resetting the flag to prevent duplicate saves
    setTimeout(() => {
      isActionInProgressRef.current = false
    }, 0)
  }

  const handleCancel = () => {
    if (isActionInProgressRef.current) return
    isActionInProgressRef.current = true
    
    setEditedTitle(title)
    setIsEditing(false)
    // Delay resetting the flag to prevent blur from triggering after cancel
    setTimeout(() => {
      isActionInProgressRef.current = false
    }, 0)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const handleBlur = () => {
    handleSave()
  }

  const handleEditClick = (e: MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation()
    }
    startEditing()
  }

  const handleInputClick = (e: MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation()
    }
  }

  const isLarge = variant === 'large'
  const textSizeClass = isLarge ? 'text-lg' : 'text-sm'
  const maxWidthClass = isLarge ? 'max-w-[40vw]' : 'max-w-[18rem]'
  const iconSizeClass = isLarge ? 'w-4 h-4' : 'w-3 h-3'
  const hoverBgClass = isLarge ? 'hover:bg-gray-50 dark:hover:bg-dark-muted' : 'hover:bg-gray-100 dark:hover:bg-dark-accent'

  // Shared className logic for title spans — the rename input reuses these
  // exact classes so entering edit mode never resizes the field.
  const titleClassName = clsx(
    'font-medium truncate',
    textSizeClass,
    maxWidthClass,
    isRTL ? 'text-right' : 'text-left',
    className
  )

  if (isEditing) {
    return (
      // Same wrapper box as display mode (padding + negative margin included)
      // so the title's footprint is identical in both modes.
      <div
        role="none"
        onClick={handleInputClick}
        className={clsx(
          'flex items-center gap-2 rounded px-2 py-1 -mx-2 min-w-0',
          isRTL ? 'flex-row-reverse' : ''
        )}
      >
        <InlineRenameInput
          ref={inputRef}
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          maxLength={maxLength}
          className={titleClassName}
        />
        {/* Invisible pencil placeholder (small variant): holds the icon's
            slot so the control's width doesn't shrink while editing. */}
        {!isLarge && <Pencil aria-hidden className={clsx(iconSizeClass, 'invisible flex-shrink-0')} />}
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div
        className={clsx(
          'group flex items-center gap-2 rounded px-2 py-1 -mx-2 transition-colors min-w-0',
          isLarge && 'cursor-pointer',
          hoverBgClass,
          isRTL ? 'flex-row-reverse' : ''
        )}
        {...(isLarge ? { onClick: handleEditClick } : {})}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={titleClassName}>
              {trimmed}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{trimmed}</p>
          </TooltipContent>
        </Tooltip>
        {!isLarge && (
          <Pencil
            className={clsx(iconSizeClass, 'text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 cursor-pointer hover:text-orange-500')}
            onClick={handleEditClick}
          />
        )}
      </div>
    </TooltipProvider>
  )
}

export default EditableTitle

